import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Toggle from "components/form/toggle/Toggle";
import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { ILoyaltyProgram } from "redux/reducers/models/loyalty";
import { ICategories, IDepartments } from "redux/reducers/models/product";
import "../loyalty.scss";

interface IProps {
    loyaltyProgram: ILoyaltyProgram;
    setLoyaltyProgram: React.Dispatch<React.SetStateAction<ILoyaltyProgram>>;
    departments: Array<IDepartments & {cateogries?: ICategories[]}>;
    selectedDepartments: Array<number>;
    setSelectedDepartments: React.Dispatch<React.SetStateAction<Array<number>>>;
}


export default function LoyaltySettings(props: IProps) {
    const {loyaltyProgram, setLoyaltyProgram, departments, selectedDepartments, setSelectedDepartments} = props;
    const {t} = useTranslation();

    function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const {id, checked} = e.target;
        ReactDOM.unstable_batchedUpdates(() => {
            if (id === "all_purchase_types_qualify" && checked) {
                //Clear selected departments if all purchase types set to true
                setSelectedDepartments([]);
            }
            setLoyaltyProgram(prev => ({...prev, [id]: +checked}));
        });
    }

    function addDepartment(department: IDepartments & {cateogries?: ICategories[]} | ICategories){
        let updatedIds = [...selectedDepartments];
        if (updatedIds?.includes(department?.id)) {
            //Remove department and all child categories
            if (department?.type === "department") {
                updatedIds = updatedIds?.filter(updatedId => updatedId !== department?.id && !department?.cateogries?.some(category => category?.id === updatedId));
            } else {
                //Remove category
                updatedIds = updatedIds?.filter(updatedId => updatedId !== department?.id);
            }
        } else {
            //Add department and all child categories
            if (department?.type === "department") {
                const allIds = [department?.id, ...department.cateogries.map(category => category?.id)];
                updatedIds = [...updatedIds, ...allIds];
            } else {
                //Add category
                updatedIds = [...updatedIds, department?.id];
            }
        }
        setSelectedDepartments(updatedIds);
    }

    return (
        <Card title={t("Settings")} subtitle={t("Manage global settings for your loyalty points program")}>
            <Card.Section title={t("Enrollment Settings")}>
                <FormLayout>
                    <FormLayout.Group>
                        <Toggle 
                            id="automatic_enrolment"
                            onChange={handleToggleChange}
                            checked={!!loyaltyProgram?.automatic_enrolment}
                            labelRight={t("Opt-in customers into program")} 
                            labelSupport={t("Automatically enroll all customer types into your loyalty program")} />
                    </FormLayout.Group>
                </FormLayout>
            </Card.Section>
            <Card.Section title={t("Earning Rules")}>
                <FormLayout>
                    <FormLayout.Group>
                    <Toggle 
                        id="all_purchase_types_qualify"
                        onChange={handleToggleChange}
                        checked={!!loyaltyProgram?.all_purchase_types_qualify}
                        labelRight={t("All purchase types qualify")} 
                        labelSupport={t("Allow or disallow customers to earn points on all purchases")} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                    <div className="ml-15">
                        <p className="loyalty-settings-title">{t("Excluded Purchase Types")}:</p>
                        {departments?.map((department, index) => {
                            return (
                                <div key={index}>
                                    <Checkbox size="small" label={department?.title} onChange={() => addDepartment(department)} checked={selectedDepartments?.includes(department?.id)} disabled={!!loyaltyProgram?.all_purchase_types_qualify}/>
                                    {selectedDepartments?.includes(department?.id) && department?.cateogries?.length > 0 ? (
                                    department?.cateogries?.map((category, categoryIndex) => {
                                        return (
                                        <div style={{textIndent: "10px"}} key={categoryIndex}>
                                            <Checkbox size="small" label={category?.title} onChange={() => addDepartment(category)} checked={selectedDepartments?.includes(category?.id)} disabled={!!loyaltyProgram?.all_purchase_types_qualify}/>
                                        </div>
                                        );
                                    })
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    </FormLayout.Group>
                    <FormLayout.Group>
                    <Toggle 
                        id="exclude_discounted_items"
                        onChange={handleToggleChange}
                        checked={!!loyaltyProgram?.exclude_discounted_items}
                        labelRight={t("Exclude discounted items")} 
                        labelSupport={t("Disallow customers to earn points from discounted items")} />
                    </FormLayout.Group>
                </FormLayout>
            </Card.Section>
        </Card>
    );
}